import { Article } from '@wix/answers-api';
import { Tag } from '@wix/design-system';
import { Trans, useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import {
  FC,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
} from 'react';

import { useOnVisible } from '../../../../../hooks/useOnVisible';
import { useArticleUnifiedBi } from '../../../../../utils/article-unified-bi';
import { useStore } from '../../../../../zustand/stores';

import css from './index.module.scss';

export type SiteSelectorButtonProps = {
  article: Article;
  onOpenPicker: () => void;
};

export const SiteSelectorButton: FC<SiteSelectorButtonProps> = ({
  article,
  onOpenPicker,
}) => {
  const { t } = useTranslation();
  const { selectedSite, setUserSelectedSite } = useStore(
    (state) => state.siteSelectorSlice
  );
  const { sitePickerVisibleTrack, sitePickerRemoveTrack } =
    useArticleUnifiedBi(article);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isVisible = useOnVisible(wrapperRef);
  const trackedArticleIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    if (trackedArticleIdRef.current === article.id) {
      return;
    }
    trackedArticleIdRef.current = article.id;
    void sitePickerVisibleTrack(selectedSite?.metaSiteId);
  }, [
    isVisible,
    article.id,
    selectedSite?.metaSiteId,
    sitePickerVisibleTrack,
  ]);

  const handleRemove = () => {
    void sitePickerRemoveTrack();
    setUserSelectedSite(null);
  };

  const siteName =
    selectedSite?.siteProperties?.siteDisplayName ?? selectedSite?.name ?? '';

  const handleWrapperClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onOpenPicker();
  };

  const handleWrapperKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onOpenPicker();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={css.root}
      onClick={handleWrapperClick}
      onKeyDown={handleWrapperKeyDown}
      role="button"
      tabIndex={0}
    >
      <Tag
        id="site-context"
        size="small"
        maxWidth="none"
        className={classNames({ [css.tagUnselected]: !selectedSite })}
        removable={Boolean(selectedSite)}
        onRemove={handleRemove}
      >
        {selectedSite ? (
          <Trans
            i18nKey="site-selector.selected"
            values={{ siteName }}
            components={{ b: <b /> }}
          />
        ) : (
          t('site-selector.not-selected')
        )}
      </Tag>
    </div>
  );
};
